/**
 * Edit Modal Component for Mobile
 * Opens a full-screen modal to edit form fields on mobile devices
 */
import { useEffect, useRef } from 'react'
import { LocationDropdown } from './LocationComponents'
import '../styles/EditModal.css'

const EditModal = ({
  isOpen,
  report,
  fields = [],
  templateConfig = {},
  editedValues = {},
  handleFieldChange,
  handleSave,
  handleCancel,
  locationMap = {},
  locationsList = [],
  locationDropdownOpen,
  setLocationDropdownOpen,
  expandedLocations,
  setExpandedLocations,
  getFieldValueFromReport,
  formatFieldValue
}) => {
  const locationTriggerRef = useRef(null)
  const modalRef = useRef(null)

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  if (!isOpen || !report) return null

  // Check if this is the job number field (read-only, synced with location)
  const isJobNumberField = (fieldId) => fieldId === templateConfig?.jobNumberFieldId

  return (
    <div className="edit-modal-overlay" onClick={handleCancel}>
      <div
        ref={modalRef}
        className="edit-modal-container"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="edit-modal-header">
          <h2 className="edit-modal-title">Edit Form</h2>
          <button
            className="edit-modal-close"
            onClick={handleCancel}
            type="button"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="edit-modal-content">
          {/* Location Dropdown - Only show if locations are available */}
          {locationsList && locationsList.length > 0 && (
            <div className="edit-modal-field">
              <label className="edit-modal-label">Location</label>
              <div style={{ position: 'relative' }}>
                <div
                  ref={locationTriggerRef}
                  onClick={() => setLocationDropdownOpen(!locationDropdownOpen)}
                  className="edit-modal-select"
                >
                  {(() => {
                    const currentLocationId = editedValues.locationId !== undefined
                      ? editedValues.locationId
                      : report.locationId
                    const locationName = currentLocationId && locationMap[currentLocationId]
                      ? locationMap[currentLocationId]
                      : null

                    // Fallback to location name from report if locationId doesn't resolve
                    const displayLocation = locationName ||
                      report.location?.name ||
                      report.locationName ||
                      report.locationPath ||
                      'Select Location'

                    return (
                      <>
                        <span style={{ color: currentLocationId ? '#334155' : '#94a3b8' }}>
                          {displayLocation}
                        </span>
                        <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                          {locationDropdownOpen ? '▲' : '▼'}
                        </span>
                      </>
                    )
                  })()}
                </div>
                {locationDropdownOpen && (
                  <LocationDropdown
                    value={editedValues.locationId !== undefined ? editedValues.locationId : (report.locationId || '')}
                    onChange={(locId) => {
                      handleFieldChange(report.id, 'locationId', locId)

                      // If job number field is configured, sync it with location name
                      if (templateConfig?.jobNumberFieldId) {
                        const locationName = locationMap[locId] || ''
                        handleFieldChange(report.id, templateConfig.jobNumberFieldId, locationName)
                      }

                      setLocationDropdownOpen(false)
                    }}
                    locations={locationsList}
                    expandedLocations={expandedLocations}
                    setExpandedLocations={setExpandedLocations}
                    onClose={() => setLocationDropdownOpen(false)}
                    triggerRef={locationTriggerRef}
                  />
                )}
              </div>
            </div>
          )}

          {/* Dynamic Fields */}
          {fields.map((field) => {
            const value = getFieldValueFromReport(report, field.fieldId, field.valueType)
            const editValue = editedValues[field.fieldId] ?? value
            const choiceOptions = templateConfig?.choiceFields?.[field.fieldId]
            const isJobNumber = isJobNumberField(field.fieldId)

            return (
              <div key={field.fieldId} className="edit-modal-field">
                <label className="edit-modal-label">
                  {field.label}
                  {field.sectionLabel && (
                    <span className="edit-modal-sublabel"> ({field.sectionLabel})</span>
                  )}
                </label>

                {field.valueType === 'toggle_val' ? (
                  // Toggle field: Yes/No/NA selector
                  <select
                    value={editValue !== null && editValue !== undefined ? editValue : ''}
                    onChange={(e) => handleFieldChange(report.id, field.fieldId, e.target.value)}
                    className="edit-modal-select"
                  >
                    <option value="">Select...</option>
                    <option value="0">No</option>
                    <option value="1">Yes</option>
                    <option value="2">NA</option>
                  </select>
                ) : choiceOptions ? (
                  // Choice field: dropdown with predefined options
                  <select
                    value={editValue || ''}
                    onChange={(e) => handleFieldChange(report.id, field.fieldId, e.target.value)}
                    className="edit-modal-select"
                  >
                    <option value="">Select...</option>
                    {choiceOptions.map((option, idx) => (
                      <option key={idx} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                ) : (
                  // Regular input for other field types
                  <input
                    type={field.valueType === 'number_val' ? 'number' : 'text'}
                    value={editValue || ''}
                    onChange={(e) => handleFieldChange(report.id, field.fieldId, e.target.value)}
                    disabled={isJobNumber}
                    className="edit-modal-input"
                    style={{
                      backgroundColor: isJobNumber ? '#f1f5f9' : '#fff',
                      cursor: isJobNumber ? 'not-allowed' : 'text'
                    }}
                    title={isJobNumber ? 'Job Number is synced with Location' : ''}
                  />
                )}
              </div>
            )
          })}
        </div>

        {/* Footer */}
        <div className="edit-modal-footer">
          <button
            className="edit-modal-button edit-modal-button-cancel"
            onClick={handleCancel}
            type="button"
          >
            Cancel
          </button>
          <button
            className="edit-modal-button edit-modal-button-save"
            onClick={() => handleSave(report)}
            type="button"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  )
}

export default EditModal
